import { Ref, computed, ref } from "vue";

import { IPodiumItem } from "@/components/data/dashboard/insights-podium/insights-podium.types";
import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { EDataUnit } from "@/constants/data-units.type";
import {
  EColumnM2Consumption,
  EColumnPeriodComparisonConsumption,
  ECurveConsumption,
  EYearConsumption,
} from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { THeatmapConsumption } from "@/data/api/kili/consumption/types/heatmap-consumption.type";
import { TMonositeMissingInfos } from "@/data/api/kili/consumption/types/monosite-consumption.type";
import { MonositeConsumption, SectionConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import { chain } from "@/utils/chain";
import { formatWhValue } from "@/utils/translation-utils";

export function useDashboardMonoController(
  siteConsumption: Ref<MonositeConsumption | undefined> | undefined,
  missingInfos: Ref<TMonositeMissingInfos | undefined> | undefined,
  heatmapConsumption: Ref<THeatmapConsumption | undefined> | undefined,
) {
  const isMissingDsoSubscriptionDialogVisible = ref<boolean>(false);
  const isMissingSectionSurfaceAreaDialogVisible = ref<boolean>(false);

  const hasNoDsoDataAndNoInstalledEquipments = computed<boolean>(() => {
    return !siteConsumption?.value?.global.value && !siteConsumption?.value?.devicesCount;
  });

  const isMissingDsoSubscriptionWarningVisible = computed<boolean>(() => {
    return (
      !!missingInfos?.value?.missingSomeDsoIndexSubscriptions || !!missingInfos?.value?.missingAllDsoIndexSubscriptions
    );
  });

  const podiumItems = computed<IPodiumItem[]>(() => {
    if (!siteConsumption?.value) return [];

    return chain(siteConsumption.value?.devicesBySection)
      .filterMap((sectionDevicesConsumption) => {
        if (!sectionDevicesConsumption.total.value) {
          return undefined;
        }

        return {
          tooltip: formatWhValue(sectionDevicesConsumption.total.value),
          name: sectionDevicesConsumption.sectionName,
          value: sectionDevicesConsumption.total.value,
        };
      })
      .sortBy((a) => a.value)
      .slice(0, 3)
      .value();
  });

  const formattedConsumptionCurvesSeries = computed<TTemporalDataResponseSerie<ECurveConsumption>[]>(() => {
    const series: TTemporalDataResponseSerie<ECurveConsumption>[] = [];

    if (siteConsumption?.value?.devicesByDay.value) {
      series.push({
        category: ECurveConsumption.DEVICE_CONSUMPTION_DETAILED_CURVE,
        value: siteConsumption.value.devicesByDay.value,
        unit: siteConsumption.value.devicesByDay.unit,
      });
    }

    if (siteConsumption?.value?.globalByDay.value.length) {
      series.push({
        category: ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
        value: siteConsumption.value.globalByDay.value,
        unit: siteConsumption.value.globalByDay.unit,
      });
    }

    return series;
  });

  function isSectionParentWithoutConsumption(sectionConsumption: SectionConsumption): boolean {
    return sectionConsumption.isParent && !sectionConsumption.total.value;
  }

  const formattedConsumptionAreaBySectionSeries = computed<
    TCategorizedDataResponseSerie<EColumnM2Consumption>[] | undefined
  >(() => {
    if (!siteConsumption?.value) return;

    const orderedSectionsWithConsumptionPerArea = chain(siteConsumption.value?.devicesBySection)
      .filterMap((sectionConsumption) => {
        const value = sectionConsumption.consumptionPerArea.value;
        if (typeof value !== "number") return undefined;

        if (isSectionParentWithoutConsumption(sectionConsumption)) return undefined;

        return {
          ...sectionConsumption,
          consumptionPerArea: {
            ...sectionConsumption.consumptionPerArea,
            value,
          },
        };
      })
      .sortBy((a) => -a.consumptionPerArea.value)
      .value();

    const sectionsWithoutConsumptionPerArea = siteConsumption.value.devicesBySection.filter(
      (sectionConsumption) => typeof sectionConsumption.consumptionPerArea.value !== "number",
    );

    const allOrderedSections = [...orderedSectionsWithConsumptionPerArea, ...sectionsWithoutConsumptionPerArea];

    return [
      {
        kind: EColumnM2Consumption.AREA_CONSUMPTION,
        unit: EDataUnit.WATT_HOUR,
        value: allOrderedSections.map((sectionConsumption) => {
          return {
            name: sectionConsumption.sectionName,
            value: sectionConsumption.consumptionPerArea.value,
          };
        }),
      },
    ];
  });

  const isMissingPartialSectionSurfaceArea = computed<boolean>(() => {
    if (!siteConsumption?.value) return false;

    return siteConsumption.value.devicesBySection.some(
      (sectionConsumption) => !sectionConsumption.consumptionPerArea.value,
    );
  });

  const isMissingAllSectionSurfaceArea = computed<boolean>(() => {
    if (!siteConsumption?.value) return false;

    return siteConsumption.value.devicesBySection.every(
      (sectionConsumption) => !sectionConsumption.consumptionPerArea.value,
    );
  });

  const formattedDevicesConsumptionBySectionComparisonSeries = computed<
    TCategorizedDataResponseSerie<EColumnPeriodComparisonConsumption>[]
  >(() => {
    if (!siteConsumption?.value) return [];

    const orderedSectionsWithDeviceConsumption = chain(siteConsumption.value?.devicesBySection)
      .filterMap((sectionDevicesConsumption) => {
        const value = sectionDevicesConsumption.total.value;
        if (typeof value !== "number") return undefined;

        if (isSectionParentWithoutConsumption(sectionDevicesConsumption)) return undefined;

        return {
          ...sectionDevicesConsumption,
          total: {
            value,
            unit: sectionDevicesConsumption.total.unit,
          },
        };
      })
      .sortBy((a) => -a.total.value)
      .value();

    const sectionsWithoutDeviceConsumption = siteConsumption.value.devicesBySection.filter(
      (sectionDeviceConsumption) => typeof sectionDeviceConsumption.total.value !== "number",
    );

    const allOrderedSections = [...orderedSectionsWithDeviceConsumption, ...sectionsWithoutDeviceConsumption];

    return [
      {
        kind: EColumnPeriodComparisonConsumption.LAST_PERIOD_CONSUMPTION,
        unit: EDataUnit.WATT_HOUR,
        value: allOrderedSections.map((sectionDevicesConsumption) => {
          return {
            name: sectionDevicesConsumption.sectionName,
            value: sectionDevicesConsumption.lastPeriodTotal.value,
          };
        }),
      },
      {
        kind: EColumnPeriodComparisonConsumption.CURRENT_PERIOD_CONSUMPTION,
        unit: EDataUnit.WATT_HOUR,
        value: allOrderedSections.map((sectionConsumption) => {
          return {
            name: sectionConsumption.sectionName,
            value: sectionConsumption.total.value,
          };
        }),
      },
    ];
  });

  const formattedYearConsumptionSerie = computed<TTemporalDataResponseSerie<EYearConsumption> | undefined>(() => {
    if (!heatmapConsumption?.value) return;

    return {
      unit: EDataUnit.WATT_HOUR,
      category: EYearConsumption.YEAR_CONSUMPTION,
      value: heatmapConsumption.value.heatmap,
    };
  });

  return {
    isMissingDsoSubscriptionDialogVisible,
    isMissingSectionSurfaceAreaDialogVisible,
    hasNoDsoDataAndNoInstalledEquipments,
    isMissingDsoSubscriptionWarningVisible,
    podiumItems,
    formattedConsumptionCurvesSeries,
    formattedConsumptionAreaBySectionSeries,
    isMissingPartialSectionSurfaceArea,
    isMissingAllSectionSurfaceArea,
    formattedDevicesConsumptionBySectionComparisonSeries,
    formattedYearConsumptionSerie,
  };
}
